import moment from "moment";

/**
 * Verifica se a data é válida.
 * @param {string} data Data formatada.
 * @param {string} [formato=DD/MM/YYYY] formato do parâmetro data. Se nulo, utiliza como padrão 'DD/MM/YYYY'.
 * @returns {boolean} true se for válida e false se não for válida.
 */
export function fn_validarData(data, formato) {
  return data ? moment(data, formato || "DD/MM/YYYY", true).isValid() : false;
}

/**
 * Verifica se o mês/ano é válido.
 * @param {string} mesAno Mês/Ano no formato 'MM/YYYY'.
 * @returns {boolean} true se for válido e false se não for válido.
 */
export function fn_validarMesAno(mesAno) {
  return mesAno ? moment(mesAno, "MM/YYYY", true).isValid() : false;
}

/**
 * Verifica se o dia/mês é válido.
 * @param {string} diaMes Dia/Mês no formato 'DD/MM'.
 * @returns {boolean} true se for válido e false se não for válido.
 */
export function fn_validarDiaMes(diaMes) {
  return diaMes ? moment(diaMes, "DD/MM", true).isValid() : false;
}

/**
 * Verifica se o ano é válido.
 * @param {string} ano Ano no formato 'YYYY'.
 * @returns {boolean} true se for válido e false se não for válido.
 */
export function fn_validarAno(ano) {
  return ano ? moment(ano, "YYYY", true).isValid() : false;
}

/**
 * Converte uma data string para uma data moment.
 * @param {string} data Data no formato 'DD/MM/YYYY'.
 * @param {string} [formato] Formato do parâmetro data. Se nulo, utiliza 'DD/MM/YYYY'.
 * @returns {moment} Data moment.
 */
export function fn_getData(data, formato) {
  return data ? moment(data, formato || "DD/MM/YYYY") : null;
}

/**
 * Converte uma data string para uma data com hora moment.
 * @param {string} data Data no formato 'DD/MM/YYYY HH:mm:ss'.
 * @param {string} [formato] Formato do parâmetro data. Se nulo, utiliza 'DD/MM/YYYY HH:mm:ss'.
 * @returns {moment} Data moment.
 */
export function fn_getDataHora(data, formato) {
  return data ? moment(data, formato || "DD/MM/YYYY HH:mm:ss") : null;
}

/**
 * Obtém a data atual no formato 'DD/MM/YYYY' ou como objeto Moment.
 * @param {boolean} [isRetDtMoment=false] Se true, retorna a data atual como objeto Moment.
 * @returns {string|moment} Data atual no formato 'DD/MM/YYYY' ou como objeto Moment.
 */
export function fn_getDataAtual(isRetDtMoment) {
  const dataAtual = moment();
  return isRetDtMoment === true ? dataAtual : dataAtual.format("DD/MM/YYYY");
}

/**
 * Retorna a data/hora atual no formato 'DD/MM/YYYY HH:mm:ss' ou como objeto Moment.
 * @param {boolean} [isRetDtMoment=false] Se true, retorna a data/hora atual como objeto Moment senão como String
 * @returns {string|moment} Data/Hora atual no formato 'DD/MM/YYYY HH:mm:ss' ou como objeto Moment.
 */
export function fn_getDataHoraAtual(retornarDate) {
  const dataAtual = moment();
  return retornarDate === true
    ? dataAtual.toDate() //incluido toDate() em 02/01/25
    : dataAtual.format("DD/MM/YYYY HH:mm:ss");
}

//Retorna a data/hora atual sem os segundos
export function fn_getDataHoraAtualSemSegundos() {
  return moment().format("DD/MM/YYYY HH:mm");
}

/**
 * Obtém a hora atual no formato 'HH:mm:ss' ou como objeto Moment.
 * @param {boolean} [retDtMoment=false] Se true, retorna a hora atual como objeto Moment.
 * @returns {(string|moment)} Hora atual no formato 'HH:mm:ss' ou como objeto Moment.
 */
export function fn_getHoraAtual(retDtMoment) {
  const dataAtual = moment();
  return retDtMoment === true ? dataAtual : dataAtual.format("HH:mm:ss");
}

/**
 * Obtém a hora atual no formato 'HH:mm' ou como objeto Moment.
 * @param {boolean} [retDtMoment=false] Se true, retorna a hora atual como objeto Moment.
 * @returns {(string|moment)} Hora atual no formato 'HH:mm' ou como objeto Moment.
 */
export function fn_getHoraAtualSemSegundos(retDtMoment) {
  const dataAtual = moment();
  return retDtMoment === true ? dataAtual : dataAtual.format("HH:mm");
}

/**
 * Retorna o mês/ano atual no formato 'MM/YYYY'.
 * @returns {string} Mês/Ano atual no formato 'MM/YYYY'.
 */
export function fn_getMesAnoAtual() {
  return moment().format("MM/YYYY");
}

/**
 * Retorna o ano atual no formato 'YYYY'
 * @returns {string} Ano atual no formato 'YYYY'.
 */
export function fn_getAnoAtual() {
  return moment().format("YYYY");
}

/**
 * Retorna o dia atual no formato 'DD'.
 * @returns {string} Dia atual no formato 'DD'.
 */
export function fn_getDiaAtual() {
  return moment().format("DD");
}

export function fn_getIntervaloMinutoAtual() {
  const inicio = moment().startOf("minute");

  return {
    inicio: inicio.toDate(),
    fim: inicio.clone().add(1, "minute").toDate(),
  };
}

//Retorna a data atual com x segundos antes
export function fn_getDataAtualSegundosAntes(segundos) {
  return moment().subtract(segundos, "seconds").toDate();
}

/**
 * Formata a data.
 * @param {(Date|string)} data Data como objeto Date ou no formato ISO (ex: '2023-05-01').
 * @param {string} [formato=DD/MM/YYYY] formato desejado. Se nulo, utiliza com padrão 'DD/MM/YYYY'.
 * @returns {string} Data no formato informado.
 */
export function fn_formatarData(data, formato) {
  return data ? moment(data).format(formato || "DD/MM/YYYY") : "";
}

/**
 * Formata a hora da data.
 * @param {(Date|string)} data Data/Hora como objeto Date ou no formato ISO (ex: '2023-05-01 15:20:32').
 * @param {string} [formato=DD/MM/YYYY HH:mm:ss] formato desejado. Se nulo, utiliza com padrão 'HH:mm:ss'.
 * @returns {string} Hora no formato informado.
 */
export function fn_formatarHora(data, formato) {
  return data ? moment(data).format(formato || "HH:mm:ss") : "";
}

/**
 * Formata a data com horário.
 * @param {(Date|string)} data Data/Hora como objeto Date ou no formato ISO (ex: '2023-05-01 15:20:32').
 * @param {string} [formato=DD/MM/YYYY HH:mm:ss] formato desejado. Se nulo, utiliza com padrão 'DD/MM/YYYY HH:mm:ss'.
 * @returns {string} Data/Hora no formato informado.
 */
export function fn_formatarDataHora(data, formato) {
  return fn_formatarData(data, formato || "DD/MM/YYYY HH:mm:ss");
}

/**
 * Converte milissegundos em uma string no formato de hora 'HH:mm:ss'.
 * @param {number} milissegundos Valor em milissegundos (inteiro ou decimal).
 * @returns {string} Representação da duração no formato 'HH:mm:ss'.
 */
export function fn_formatarMilissegundosToHora(milissegundos) {
  const milissegundosInteiros = Math.floor(milissegundos);
  return moment.utc(milissegundosInteiros).format("HH:mm:ss");
}

/*
 * Recebe a data formatada DD/MM/YYYY ou DD/MM/YYYY HH:mm:ss
 * e devolve no formato YYYY-MM-DD ou YYYY-MM-DD HH:mm:ss
 */
export function fn_dataFormatToFormatBanco(data) {
  if (data && data.length == 10) {
    return moment(data, "DD/MM/YYYY").format("YYYY-MM-DD");
  } else if (data && data.length == 19) {
    return moment(data, "DD/MM/YYYY HH:mm:ss").format("YYYY-MM-DD HH:mm:ss");
  } else {
    return data;
  }
}

/*
 * Recebe o mês/ano formatado MM/YYYY e devolve no formato YYYY-MM
 */
export function fn_mesAnoFormatToFormatBanco(mesAno) {
  if (mesAno && mesAno.length == 7) {
    return moment(mesAno, "MM/YYYY").format("YYYY-MM");
  } else {
    return mesAno;
  }
}

/**
 * Retorna a data do primeiro dia do mês formatada DD/MM/YYY.
 * @param {string} data Data no formato 'DD/MM/YYYY' ou mês/ano no formato 'MM/YYYY'.
 * @param {boolean} [retDtMoment=false] Se true, retorna a data moment senão retorna a data formatada.
 * @returns {string} {(string|moment)} Data do primeiro dia do mês no formato 'DD/MM/YYYY' ou Data moment.
 */
export function fn_getDataPrimeiroDiaMes(data, retDtMoment) {
  let formato = "DD/MM/YYYY";

  if (data && data.length == 7) {
    formato = "MM/YYYY";
  }

  const mData = data ? moment(data, formato) : moment();
  const dataPrimDia = mData.startOf("month");

  return retDtMoment === true ? dataPrimDia : dataPrimDia.format("DD/MM/YYYY");
}

/**
 * Retorna a data do último dia do mês no formato 'DD/MM/YYYY'.
 * @param {string} data Data no formato 'DD/MM/YYYY' ou mês/ano no formato 'MM/YYYY'.
 * @param {boolean} [retDtMoment=false] Se true, retorna a data moment senão retorna a data formatada.
 * @returns {(string|moment)} Data do último dia do mês no formato 'DD/MM/YYYY' ou Data moment.
 */
export function fn_getDataUltimoDiaMes(data, retDtMoment) {
  let formato = "DD/MM/YYYY";

  if (data && data.length == 7) {
    formato = "MM/YYYY";
  }
  const mData = data ? moment(data, formato) : moment();
  const dataUltDiaMes = mData.endOf("month");

  return retDtMoment === true
    ? dataUltDiaMes
    : dataUltDiaMes.format("DD/MM/YYYY");
}

/**
 * Retorna o último dia do mês.
 * @param {string} data Data no formato 'DD/MM/YYYY' ou 'MM/YYYY'.
 * @returns {number} Último dia do mês.
 */
export function fn_getUltimoDiaMes(data) {
  let formato = "DD/MM/YYYY";

  if (data && data.length == 7) {
    formato = "MM/YYYY";
  }

  const mData = data ? moment(data, formato) : moment();
  return mData.endOf("month").date();
}

/**
 * Retorna o mês/ano da data, no formato 'MM/YYYY'.
 * @param {string} data Data formatada.
 * @param {string} [formato=DD/MM/YYYY] Formato do parâmetro data. Se for nulo, utiliza formato 'DD/MM/YYYY'.
 * @returns {string} Mês/Ano da data no formato "MM/YYYY".
 */
export function fn_getMesAnoData(data, formato) {
  const mData = moment(data, formato || "DD/MM/YYYY");
  return mData.format("MM/YYYY");
}

/**
 * Retorna o dia/mês da data, no formato 'DD/MM'.
 * @param {string} data Data formatada.
 * @param {string} [formato=DD/MM/YYYY] Formato do parâmetro data. Se for nulo, utiliza formato 'DD/MM/YYYY'.
 * @returns {string} Dia/Mês da data no formato "DD/MM".
 */
export function fn_getDiaMesData(data, formato) {
  const mData = moment(data, formato || "DD/MM/YYYY");
  return mData.format("DD/MM");
}

/**
 * Retorna o dia no formato 'DD' de determinada data no formato 'DD/MM/YYYY'.
 * @param {string} data Data no formato 'DD/MM/YYYY'.
 * @returns {string} Dia da data no formato 'DD'.
 */
export function fn_getDiaData(data) {
  const mData = moment(data, "DD/MM/YYYY");
  return mData.format("DD");
}

/**
 * Retorna o mês no formato 'MM' de determinada data no formato 'DD/MM/YYYY'.
 * @param {string} data Data no formato 'DD/MM/YYYY'.
 * @returns {string} Mês da data no formato 'MM'.
 */
export function fn_getMesData(data) {
  const mData = moment(data, "DD/MM/YYYY");
  return mData.format("MM");
}

/**
 * Retorna o ano no formato YYYY de determinada data no formato DD/MM/YYYY
 * @param {string} data Data formatada.
 * @param {string} [formato] Formato do parâmetro data. Se for nulo, utiliza padrão conforme tamanho da data:
 *                           Tamanho 7: 'MM/YYYY'
 *                           Senão: 'DD/MM/YYYY'
 * @returns {string} Ano da data no formato 'YYYY'.
 */
export function fn_getAnoData(data, formato) {
  if (!formato) {
    if (data.length == 7) {
      formato = "MM/YYYY";
    } else {
      formato = "DD/MM/YYYY";
    }
  }

  const mData = moment(data, formato);
  return mData.format("YYYY");
}

/**
 * Retorna a diferença de dias entre duas datas
 * @param {string} dataInicial Data inicial no formato 'DD/MM/YYYY'.
 * @param {string} dataFinal Data final no formato 'DD/MM/YYYY'.
 * @returns {number} Qtde de dias da diferença.
 */
export function fn_getDifDiasDatas(dataInicial, dataFinal) {
  const dataI = moment(dataInicial, "DD/MM/YYYY");
  const dataF = moment(dataFinal, "DD/MM/YYYY");

  return moment(dataF).diff(dataI, "days");
}

/**
 * Retorna a diferença de meses entre duas datas.
 * @param {string} dataInicial Data inicial no formato 'MM/YYYY'.
 * @param {string} dataFinal Data final no formato 'MM/YYYY'.
 * @returns {number}  Qtde de meses da diferença.
 */
export function fn_getDifMesesDatas(dataInicial, dataFinal) {
  const dataI = moment(dataInicial, "MM/YYYY");
  const dataF = moment(dataFinal, "MM/YYYY");

  return moment(dataF).diff(dataI, "month");
}

/**
 * Retorna a diferença de minutos entre duas datas.
 * @param {string} dataInicial Data inicial no formato 'DD/MM/YYYY HH:mm:ss'.
 * @param {string} dataFinal Data final no formato 'DD/MM/YYYY HH:mm:ss'.
 * @returns {number}  Qtde de minutos da diferença.
 */
export function fn_getDifMinutosDatas(dataInicial, dataFinal) {
  const dataI = moment(dataInicial, "DD/MM/YYYY HH:mm:ss");
  const dataF = moment(dataFinal, "DD/MM/YYYY HH:mm:ss");

  return moment(dataF).diff(dataI, "minutes");
}

/**
 * Verifica se a data 1 é anterior a data 2 <br/>
 * Se o formato for nulo, considera os seguintes formatos de acordo com o tamanho da Data 1: <br/>
 *  - Tamanho 10: 'DD/MM/YYYY' <br/>
 *  - Tamanho 19: 'DD/MM/YYYY HH:mm:ss' <br/>
 *  - Tamanho 7:  'MM/YYYY' <br/>
 *  - Tamanho 8:  'HH:mm:ss' <br/>
 *  - Tamanho 5:  'HH:mm' <br/>
 * @param {string} date1 Data 1 formatada.
 * @param {string} date2 Data 2 formatada.
 * @param {boolean} [inclusive=false] Se true, inclui a data atual na verificação: Data 1 anterior ou igual a Data 2
 * @param {string} [formato] Formato das datas passadas como parâmetro.
 * @returns {boolean} true se for anterior ou false se não for anterior.
 */
export function fn_dataIsBefore(date1, date2, inclusive, formato) {
  if (!formato) {
    if (date1.length == 10) {
      formato = "DD/MM/YYYY";
    } else if (date1.length == 19) {
      formato = "DD/MM/YYYY HH:mm:ss";
    } else if (date1.length == 7) {
      formato = "MM/YYYY";
    } else if (date1.length == 8) {
      formato = "HH:mm:ss";
    } else if (date1.length == 5) {
      formato = "HH:mm";
    }
  }

  const mDate1 = moment(date1, formato);
  const mDate2 = moment(date2, formato);

  return inclusive ? mDate1.isSameOrBefore(mDate2) : mDate1.isBefore(mDate2);
}

/**
 * Verifica se a data 1 é posterior a data 2. <br/>
 * Se o formato for nulo, considera os seguintes formatos de acordo com o tamanho da data 1: <br/>
 *  - Tamanho 10: 'DD/MM/YYYY' <br/>
 *  - Tamanho 19: 'DD/MM/YYYY HH:mm:ss' <br/>
 *  - Tamanho 7:  'MM/YYYY' <br/>
 *  - Tamanho 8:  'HH:mm:ss' <br/>
 *  - Tamanho 5:  'HH:mm' <br/>
 *
 * @param {string} date1 Data 1 formatada.
 * @param {string} date2 Data 2 formatada.
 * @param {boolean} [inclusive=false] Se true, inclui a data atual na verificação: Data 1 posterior ou igual a Data 2.
 * @param {string} [formato] Formato das datas passadas como parâmetro.
 * @returns {boolean}  true se for posterior ou false se não for posterior.
 */
export function fn_dataIsAfter(date1, date2, inclusive, formato) {
  if (!formato) {
    if (date1.length == 10) {
      formato = "DD/MM/YYYY";
    } else if (date1.length == 19) {
      formato = "DD/MM/YYYY HH:mm:ss";
    } else if (date1.length == 7) {
      formato = "MM/YYYY";
    } else if (date1.length == 8) {
      formato = "HH:mm:ss";
    } else if (date1.length == 5) {
      formato = "HH:mm";
    }
  }

  const mDate1 = moment(date1, formato);
  const mDate2 = moment(date2, formato);

  return inclusive ? mDate1.isSameOrAfter(mDate2) : mDate1.isAfter(mDate2);
}

/**
 * Verifica se a data 1 é igual a data 2. <br/>
 * Se for nulo, considera os seguintes formatos de acordo com o tamanho da Data 1: <br/>
 *  - Tamanho 10: 'DD/MM/YYYY' <br/>
 *  - Tamanho 19: 'DD/MM/YYYY HH:mm:ss' <br/>
 * @param {string} date1 Data 1 formatada.
 * @param {string} date2 Data 2 formatada.
 * @param {string} [formato] Formato das datas passadas como parâmetro.
 * @returns {boolean}  true se for igual ou false se não for igual.
 */
export function fn_dataIsSame(date1, date2, formato) {
  if (!formato) {
    if (date1.length == 10) {
      formato = "DD/MM/YYYY";
    } else if (date1.length == 19) {
      formato = "DD/MM/YYYY HH:mm:ss";
    }
  }

  const mDate1 = moment(date1, formato);
  const mDate2 = moment(date2, formato);

  return mDate1.isSame(mDate2, date1.length == 10 ? "day" : null);
}

/**
 * Verifica se a data está entre
 * @param {string} data Data que será verificada formatada.
 * @param {string} dataInicio Data início do período formatada
 * @param {string} dataFim Data fim do período formatada
 * @param {boolean} [inclusive=true] Se true, inclui a data início e fim do período
 * @param {string} [formato] Formato das datas passadas como parâmetro. Se não passar será formatada como DD/MM/YYYY
 * @returns
 */
export function fn_dataIsBetween(
  data,
  dataInicio,
  dataFim,
  inclusive = true,
  formato,
) {
  let unidade;

  if (!formato) {
    if (data.length == 10) {
      formato = "DD/MM/YYYY";
      unidade = "day";
    } else if (data.length == 19) {
      formato = "DD/MM/YYYY HH:mm:ss";
    } else if (data.length == 7) {
      formato = "MM/YYYY";
      unidade = "month";
    } else if (data.length == 4) {
      formato = "YYYY";
      unidade = "year";
    } else if (data.length == 8) {
      formato = "HH:mm:ss";
    } else if (data.length == 5) {
      formato = "HH:mm";
    }
  }

  const mDate = moment(data, formato);
  const mDateInicio = moment(dataInicio, formato);
  const mDateFim = moment(dataFim, formato);

  return mDate.isBetween(
    mDateInicio,
    mDateFim,
    unidade,
    inclusive ? "[]" : "()",
  );
}

/**
 * Retorna a nova data com os dias adicionados (soma ou subtração) <br/>
 * Se não for passada a data, será considerada a data atual.
 * @param {string} data Data no formato 'DD/MM/YYYY' ou 'DD/MM/YYYY HH:mm:ss'.
 * @param {number} qtdeDias Qtde de dias que serão adicionados ou subtraídos na data.
 * @param {boolean} [retDtMoment=false] Se true, retorna a data moment senão retorna a data formatada.
 * @returns {(string|moment)} Data no formato recebido ou Data moment, com os dias adicionados.
 */
export function fn_addDay(data, qtdeDias, retDtMoment) {
  let formato = "DD/MM/YYYY";

  if (data && data.length == 19) {
    formato = "DD/MM/YYYY HH:mm:ss";
  }

  const mData = data ? moment(data, formato) : moment();
  const novaData = mData.clone().add(Number(qtdeDias), "days");

  return retDtMoment === true ? novaData : novaData.format(formato);
}

/**
 * Retorna a nova data com os meses adicionados (soma ou subtração) <br/>
 * Se não for passada a data, será considerada a data atual
 *
 * @param {string} data Data no formato 'DD/MM/YYYY' ou 'DD/MM/YYYY HH:mm:ss' ou 'MM/YYYY'.
 * @param {number} qtdeMeses Qtde de meses que serão adicionados ou subtraídos na data.
 * @param {boolean} [semFormatar=false] Se true, retorna a data moment senão retorna a data formatada.
 *
 * @returns {(string|moment)} Data no formato recebido ou Data moment, com os meses adicionados.
 */
export function fn_addMonth(data, qtdeMeses, semFormatar) {
  let formato = "DD/MM/YYYY";

  if (data) {
    if (data.length == 19) {
      formato = "DD/MM/YYYY HH:mm:ss";
    } else if (data.length == 7) {
      formato = "MM/YYYY";
    }
  }

  const mData = data ? moment(data, formato) : moment();
  const novaData = mData.clone().add(Number(qtdeMeses), "month");

  return semFormatar === true ? novaData : novaData.format(formato);
}

/**
 * Retorna a lista com os dias da semana.
 * @param {boolean} [incluirFeriado=false] Se true, inclui a opção de "Feriado" com valor 7.
 * @returns {array} Array com os dias da semana (0=Domingo; 1=Segunda-feira... 6=Sábado; 7=Feriado)
 */
export function fn_listarDiasSemana(incluirFeriado) {
  const diasSemana = [
    { value: "0", label: "Domingo", abreviatura: "Dom", ordem: 7 },
    { value: "1", label: "Segunda-feira", abreviatura: "Seg", ordem: 1 },
    { value: "2", label: "Terça-feira", abreviatura: "Ter", ordem: 2 },
    { value: "3", label: "Quarta-feira", abreviatura: "Qua", ordem: 3 },
    { value: "4", label: "Quinta-feira", abreviatura: "Qui", ordem: 4 },
    { value: "5", label: "Sexta-feira", abreviatura: "Sex", ordem: 5 },
    { value: "6", label: "Sábado", abreviatura: "Sab", ordem: 6 },
  ];

  if (incluirFeriado === true) {
    diasSemana.push({
      value: "7",
      label: "Feriado",
      abreviatura: "Fer",
      ordem: 8,
    });
  }

  return diasSemana;
}

/**
 * Retorna o código do dia da semana de determinada data no formato 'DD/MM/YYYY'.
 * @param {string} data Data no formato 'DD/MM/YYYY'.
 * @returns {number} Código do dia da semana da data.
 */
export function fn_getCodigoDiaSemanaData(data) {
  const mData = moment(data, "DD/MM/YYYY");
  return mData.day();
}

/**
 * Retorna a descrição do dia da semana de determinada data no formato 'DD/MM/YYYY'.
 * @param {string} data Data no formato 'DD/MM/YYYY'.
 * @returns {string} Descrição do dia da semana da data.
 */
export function fn_getDescDiaSemanaData(data) {
  const diaSemanaNumerico = fn_getCodigoDiaSemanaData(data);

  const nomesDiasSemana = [
    "Domingo",
    "Segunda-feira",
    "Terça-feira",
    "Quarta-feira",
    "Quinta-feira",
    "Sexta-feira",
    "Sábado",
  ];

  return nomesDiasSemana[diaSemanaNumerico];
}

/**
 * Retorna a descrição abreviada do dia da semana de determinada data no formato 'DD/MM/YYYY'.
 * @param {string} data Data no formato 'DD/MM/YYYY'.
 * @returns {string} Descrição abreviada do dia da semana da data.
 */
export function fn_getDescAbrevDiaSemanaData(data) {
  const diaSemanaNumerico = fn_getCodigoDiaSemanaData(data);

  const nomesDiasSemana = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"];

  return nomesDiasSemana[diaSemanaNumerico];
}

/**
 * Retorna a descrição do dia da semana de acordo com o código <br/>
 * 0=Domingo; 1=Segunda-feira... 6=Sábado; 7=Feriado.
 * @param {number} codigo Código do dia da semana.
 * @returns {string} Descrição do dia da semna.
 */
export function fn_getDescDiaSemanaCodigo(codigo) {
  const nomesDiasSemana = [
    "Domingo",
    "Segunda-feira",
    "Terça-feira",
    "Quarta-feira",
    "Quinta-feira",
    "Sexta-feira",
    "Sábado",
    "Feriado",
  ];

  return nomesDiasSemana[codigo];
}

/**
 * Retorna um objeto moment com a data do primeiro dia do mês/ano informado às 00:00:00.
 * @param {string} mesAno Mês/ano no formato 'MM/YYYY'.
 * @returns {moment} Data moment no primeiro dia do mês, incluindo hora 00:00:00.
 */
export function fn_getMesAnoStartOf(mesAno) {
  return moment(mesAno, "MM/YYYY").startOf("month");
}

/**
 * Retorna um objeto moment com a data do último dia do mês/ano informado às 23:59:59.
 * @param {string} mesAno Mês/ano no formato 'MM/YYYY'.
 * @returns {moment} Data moment no último dia do mês, incluindo hora 23:59:59.
 */
export function fn_getMesAnoEndOf(mesAno) {
  return moment(mesAno, "MM/YYYY").endOf("month");
}

/**
 * Retorna um objeto moment com a data do primeiro dia do mês/ano informado às 00:00:00.
 * @param {string} data Data no formato 'DD/MM/YYYY'.
 * @returns {moment} Data moment no primeiro dia do mês, incluindo hora 00:00:00.
 */
export function fn_getDataStartOf(data) {
  return moment(data, "DD/MM/YYYY").startOf("day");
}

/**
 * Retorna um objeto moment com a data do último dia do mês/ano informado às 23:59:59.
 * @param {string} data Data no formato 'DD/MM/YYYY'.
 * @returns {moment} Data moment no último dia do mês, incluindo hora 23:59:59.
 */
export function fn_getDataEndOf(data) {
  return moment(data, "DD/MM/YYYY").endOf("day");
}

/**
 * Retorna um array com os nomes dos meses. </br>
 * Ex: JANEIRO, FEVEREIRO... NOVEMBRO, DEZEMBRO.
 *
 * @returns {array} Lista com os nomes dos meses.
 */
export function fn_loadOpcoesMeses() {
  return [
    { value: 1, label: "JANEIRO" },
    { value: 2, label: "FEVEREIRO" },
    { value: 3, label: "MARÇO" },
    { value: 4, label: "ABRIL" },
    { value: 5, label: "MAIO" },
    { value: 6, label: "JUNHO" },
    { value: 7, label: "JULHO" },
    { value: 8, label: "AGOSTO" },
    { value: 9, label: "SETEMBRO" },
    { value: 10, label: "OUTUBRO" },
    { value: 11, label: "NOVEMBRO" },
    { value: 12, label: "DEZEMBRO" },
  ];
}

/**
 * Retorna um array com os meses do período passado como parâmetro no formato 'MM/YYYY'.
 * @param {string} mesAnoInicio Mês/Ano inicial no formato 'MM/YYYY'.
 * @param {string} mesAnoFim Mês/Ano final no formato 'MM/YYYY'.
 * @returns {array} Array com os meses do período no formato 'MM/YYYY'.
 */
export function fn_getListMesAnoPeriodo(mesAnoInicio, mesAnoFim) {
  const result = [];

  const dataMesaAnoI = moment(mesAnoInicio, "MM/YYYY");
  const dataMesAnoF = moment(mesAnoFim, "MM/YYYY");

  while (dataMesaAnoI.isSameOrBefore(dataMesAnoF, "month")) {
    result.push(dataMesaAnoI.format("MM/YYYY"));
    dataMesaAnoI.add(1, "month");
  }
  return result;
}

/**
 * Retorna um array com os últimos x meses no formato MM/YYYY
 * @param {number} qtdeMeses
 * @returns  {array} Lista com os últimos x meses.
 */
export function fn_getUltimosMeses(qtdeMeses) {
  return Array.from({ length: qtdeMeses }, (_, i) =>
    moment().subtract(i, "months").format("MM/YYYY"),
  );
}

/**
 * Retorna a diferença entre duas datas no formato 'DD/MM/YYYY' ou mês/ano no formato 'MM/YYYY' para realizar a ordenção. <br/>
 * Se a diferença for: <br/>
 * - positiva: data1 > data2 <br/>
 * - negativa: data1 < data2 <br/>
 * - zero: data1 = data2
 * @param {string} data1 Data 1 formatada.
 * @param {string} data2 Data 2 formatada.
 * @param {string} [formato] Formato das datas. Se nulo, define o formato ('DD/MM/YYYY' ou 'MM/YYYY') de acordo com o tamanho da data.
 * @returns {number} Diferença em milissegundos entre as datas.
 */
export function fn_getDifDatasSort(data1, data2, formato) {
  if (!formato) {
    if (data1.length == 7) {
      formato = "MM/YYYY";
    } else {
      formato = "DD/MM/YYYY";
    }
  }

  return moment(data1, formato).toDate() - moment(data2, formato).toDate();
}

/**
 * Retorna a data início da posição do dia
 * A posição do dia deve contemplar as datas não úteis (sábado, domingo e feriado) que já passaram e ficaram para o pagamento do próximo dia útil.
 * Ex1: Data fim posição dia: 08//06/2026 (seg). O período vai ser 06/06/2026 a 08/06/2026 (sábado, domingo e segunda)
 * Ex2: Data fim posição dia: 09//06/2026 (ter). O período vai ser 08/06/2026 a 08/06/2026 (terça)
 * @param {string} dataFimPosicaoDia  Data no formato DD/MM/YYYY
 * @param {array} datasFeriados Array com as datas de feriado no formato DD/MM/YYYY
 * @returns {string} Data início do período no formato DD/MM/YYYY
 */
export function fn_getDataInicioPosicaoDia(
  dataFimPosicaoDia,
  datasFeriados = [],
) {
  let mDate = moment(dataFimPosicaoDia, "DD/MM/YYYY", true);

  function isNaoUtil(date) {
    const diaSemana = date.day();
    const isFimDeSemana = diaSemana === 0 || diaSemana === 6;

    const dataFormatada = date.format("DD/MM/YYYY");
    const isFeriado = datasFeriados.includes(dataFormatada);

    return isFimDeSemana || isFeriado;
  }

  const diaAnterior = mDate.clone().subtract(1, "day");

  //Se dia atual NÃO útil
  if (isNaoUtil(mDate)) {
    // volta até o início do bloco
    while (isNaoUtil(mDate.clone().subtract(1, "day"))) {
      mDate = mDate.subtract(1, "day");
    }
    return mDate.format("DD/MM/YYYY");
  }

  // Se dia atual útil e anterior também útil → retorna o próprio dia
  if (!isNaoUtil(diaAnterior)) {
    return mDate.format("DD/MM/YYYY");
  }

  //Se anterior NÃO útil → volta até início do bloco
  mDate = diaAnterior;

  while (isNaoUtil(mDate.clone().subtract(1, "day"))) {
    mDate = mDate.subtract(1, "day");
  }

  return mDate.format("DD/MM/YYYY");
}

/*
 * Verifica se o período está ativo dentro do mês/ano
 */
export function fn_periodoIsAtivoMesAno(mesAno, dataInicio, dataFim) {
  if (!dataInicio || !dataFim) return true;

  const dataInicioAtiva = fn_dataIsBefore(
    fn_getMesAnoData(dataInicio),
    mesAno,
    true,
  );

  const dataFimAtiva = fn_dataIsAfter(fn_getMesAnoData(dataFim), mesAno, true);

  const isAtivo = dataInicioAtiva && dataFimAtiva;
  return isAtivo;
}

/*
 * Verifica se o dia/mês do feriado está dentro de determinado período de datas
 */
export function fn_feriadoIsBetween(dataInicio, dataFim, diaMesFixo) {
  //NOVA VERSAO DESSA FUNCAO PARA TESTAR - /07/261
  const inicio = moment(dataInicio, "DD/MM/YYYY");
  const fim = moment(dataFim, "DD/MM/YYYY");

  for (let ano = inicio.year(); ano <= fim.year(); ano++) {
    const feriado = moment(`${diaMesFixo}/${ano}`, "DD/MM/YYYY");

    if (feriado.isBetween(inicio, fim, "day", "[]")) {
      return true;
    }
  }

  return false;

  // // Converte a string "DD/MM" para um objeto moment válido para o ano da data de início
  // const anoInicio = moment(dataInicio, "DD/MM/YYYY").format("YYYY");
  // const feriadoInicio = moment(`${diaMesFixo}/${anoInicio}`, "DD/MM/YYYY");

  // // Converte a string "DD/MM" para um objeto moment válido para o ano da data de fim
  // const anoFim = moment(dataFim, "DD/MM/YYYY").format("YYYY");
  // const feriadoFim = moment(`${diaMesFixo}/${anoFim}`, "DD/MM/YYYY");

  // // Verifica se o feriado está entre as datas de início e fim usando comparações diretas
  // const isFeriadoInicioBetween =
  //   feriadoInicio.isSameOrAfter(moment(dataInicio, "DD/MM/YYYY")) &&
  //   feriadoInicio.isSameOrBefore(moment(dataFim, "DD/MM/YYYY"));

  // const isFeriadoFimBetween =
  //   feriadoFim.isSameOrAfter(moment(dataInicio, "DD/MM/YYYY")) &&
  //   feriadoFim.isSameOrBefore(moment(dataFim, "DD/MM/YYYY"));

  // return isFeriadoInicioBetween || isFeriadoFimBetween;
}
